"use client"

import { useState } from "react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { 
  Check, 
  X, 
  Sparkles, 
  Zap, 
  Crown, 
  Star, 
  TrendingUp,
  Shield,
  Rocket,
  Users
} from "lucide-react"

export function PricingSection() {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly')
  const [hoveredPlan, setHoveredPlan] = useState<number | null>(null)

  const plans = [
    {
      name: "Starter",
      description: "Perfect for personal events and small gatherings",
      price: "Free",
      period: "forever",
      popular: false,
      icon: Sparkles,
      gradient: "from-blue-500 to-cyan-500",
      features: [
        "10 AI generations per month",
        "3 active event pages",
        "Basic templates",
        "Mobile responsive design",
        "Basic RSVP system",
        "Community support",
        "1GB file storage"
      ],
      limitations: [
        "Custom domain",
        "Payment integration", 
        "Advanced analytics",
        "Priority support"
      ],
      cta: "Get Started Free",
      ctaLink: "/signup"
    },
    {
      name: "Professional", 
      description: "Ideal for event organizers and growing businesses",
      price: billingPeriod === 'monthly' ? "$29" : "$24",
      period: billingPeriod === 'monthly' ? "per month" : "per month, billed yearly",
      popular: true,
      icon: Zap,
      gradient: "from-theme-primary to-theme-accent",
      features: [
        "Unlimited AI generations",
        "Unlimited event pages", 
        "Premium templates",
        "Advanced customization",
        "Complete RSVP system",
        "Payment integration (Stripe)",
        "Advanced analytics & insights",
        "Custom domain support",
        "50GB file storage",
        "Priority email support",
        "Team collaboration (5 members)"
      ],
      limitations: [
        "White-label option"
      ],
      cta: "Start Professional Trial",
      ctaLink: "/signup?plan=pro"
    },
    {
      name: "Enterprise",
      description: "For large organizations and agencies with custom needs", 
      price: "Custom",
      period: "contact for pricing",
      popular: false,
      icon: Crown,
      gradient: "from-orange-500 to-red-500",
      features: [
        "Everything in Professional",
        "Unlimited team members",
        "White-label solution", 
        "Custom integrations",
        "Advanced security (SSO)",
        "SLA guarantee (99.9%)",
        "Dedicated account manager",
        "Custom AI model training",
        "API access & webhooks",
        "24/7 phone & chat support",
        "Custom onboarding",
        "Priority feature requests"
      ],
      limitations: [],
      cta: "Contact Sales",
      ctaLink: "/contact-sales"
    }
  ]

  return (
    <section className="relative py-24 overflow-hidden bg-background">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-30 dark:opacity-20">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(90deg, rgba(var(--theme-primary), 0.02) 1px, transparent 1px),
              linear-gradient(rgba(var(--theme-primary), 0.02) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      {/* Floating Gradient Orbs */}
      <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl animate-pulse"
        style={{
          background: `radial-gradient(circle, rgb(var(--theme-primary)) 0%, transparent 70%)`,
          animationDuration: '8s'
        }}
      />
      <div className="absolute bottom-20 right-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl animate-pulse"
        style={{
          background: `radial-gradient(circle, rgb(var(--theme-secondary)) 0%, transparent 70%)`,
          animationDuration: '6s',
          animationDelay: '2s'
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center animate-in fade-in slide-in-from-bottom duration-700">
          <Badge 
            variant="secondary" 
            className="mb-6 px-4 py-2 text-sm glass-card border"
            style={{
              borderColor: `rgba(var(--theme-primary), 0.3)`,
              background: `rgba(var(--theme-primary), 0.05)`
            }}
          >
            <Star className="w-4 h-4 mr-2" style={{ color: `rgb(var(--theme-primary))` }} />
            Pricing Plans
          </Badge>
          
          <h2 className="text-3xl font-bold text-foreground sm:text-5xl mb-6">
            Start Free with 10 AI Generations{" "}
            <span 
              className="relative inline-block"
              style={{
                background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              Scale as You Grow
              <svg
                className="absolute -bottom-2 left-0 w-full"
                height="8"
                viewBox="0 0 200 8"
                fill="none"
              >
                <path
                  d="M1 5.5C50 2.5 150 2.5 199 5.5"
                  stroke={`rgb(var(--theme-accent))`}
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </h2>
          
          <p className="mt-6 text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            All plans include our core AI-powered event creation features.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="mt-12 flex justify-center animate-in fade-in slide-in-from-bottom duration-700 delay-100">
          <div className="glass-card rounded-xl p-1.5 flex border"
            style={{ borderColor: `rgba(var(--theme-primary), 0.2)` }}
          >
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                billingPeriod === 'monthly'
                  ? 'text-white shadow-lg'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              style={{
                background: billingPeriod === 'monthly' 
                  ? `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                  : 'transparent'
              }}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                billingPeriod === 'yearly'
                  ? 'text-white shadow-lg'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              style={{
                background: billingPeriod === 'yearly' 
                  ? `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                  : 'transparent'
              }}
            >
              Yearly
              <Badge 
                className="text-xs px-2 py-0.5"
                style={{
                  background: billingPeriod === 'yearly' 
                    ? 'rgba(255, 255, 255, 0.2)'
                    : `rgba(var(--theme-primary), 0.1)`,
                  color: billingPeriod === 'yearly' 
                    ? 'white'
                    : `rgb(var(--theme-primary))`
                }}
              >
                Save 20%
              </Badge>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="mt-20 grid gap-8 lg:grid-cols-3">
          {plans.map((plan, index) => {
            const IconComponent = plan.icon
            const isHovered = hoveredPlan === index
            
            return (
              <Card
                key={index}
                className={`group relative transition-all duration-500 glass-card border backdrop-blur-sm hover:-translate-y-2 ${
                  plan.popular 
                    ? 'shadow-2xl scale-105' 
                    : 'hover:shadow-xl'
                }`}
                style={{
                  borderColor: plan.popular 
                    ? `rgba(var(--theme-primary), 0.5)` 
                    : isHovered 
                    ? `rgba(var(--theme-primary), 0.3)` 
                    : `rgba(var(--border), 0.5)`
                }}
                onMouseEnter={() => setHoveredPlan(index)}
                onMouseLeave={() => setHoveredPlan(null)}
              >
                {/* Gradient Overlay */}
                <div 
                  className={`absolute inset-0 rounded-lg opacity-0 transition-opacity duration-500 ${
                    plan.popular ? 'group-hover:opacity-10' : 'group-hover:opacity-5'
                  }`}
                  style={{
                    background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                  }}
                />

                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <Badge 
                      className="text-white border-0 shadow-lg px-4 py-1.5"
                      style={{
                        background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                      }}
                    >
                      <Rocket className="w-3 h-3 mr-1.5" />
                      Most Popular
                    </Badge>
                  </div>
                )}

                <CardHeader className="relative text-center pt-8 pb-6">
                  {/* Icon */}
                  <div className="mb-6 flex justify-center">
                    <div 
                      className={`w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg transition-all duration-500 ${
                        isHovered ? 'scale-110 rotate-6' : ''
                      }`}
                      style={{
                        background: plan.popular || isHovered
                          ? `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                          : `rgba(var(--theme-primary), 0.1)`
                      }}
                    >
                      <IconComponent 
                        className="w-8 h-8 transition-colors duration-500" 
                        style={{ color: plan.popular || isHovered ? 'white' : `rgb(var(--theme-primary))` }}
                      />
                    </div>
                  </div>

                  {/* Plan Name */}
                  <h3 className="text-2xl font-bold text-foreground mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {plan.description}
                  </p>

                  {/* Price */}
                  <div className="mt-6 mb-6">
                    <div 
                      className="text-5xl font-bold mb-2 transition-all duration-500"
                      style={{
                        background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        transform: isHovered ? 'scale(1.05)' : 'scale(1)'
                      }}
                    >
                      {plan.price}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {plan.period}
                    </p>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center p-3 glass-card rounded-lg border"
                      style={{ borderColor: `rgba(var(--theme-primary), 0.2)` }}
                    >
                      <div className="font-semibold text-foreground">
                        {plan.name === 'Starter' ? '10/month' : 'Unlimited'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Generations
                      </div>
                    </div>
                    <div className="text-center p-3 glass-card rounded-lg border"
                      style={{ borderColor: `rgba(var(--theme-primary), 0.2)` }}
                    >
                      <div className="font-semibold text-foreground">
                        {plan.name === 'Starter' ? '3 Events' : 'Unlimited'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Events
                      </div>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <Link to={plan.ctaLink}>
                    <Button
                      className={`w-full py-6 text-lg font-semibold shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 ${
                        plan.popular
                          ? 'text-white'
                          : ''
                      }`}
                      style={{
                        background: plan.popular || plan.name === 'Enterprise'
                          ? `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                          : undefined,
                        color: plan.popular || plan.name === 'Enterprise' ? 'white' : undefined
                      }}
                      variant={plan.popular || plan.name === 'Enterprise' ? "default" : "outline"}
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                </CardHeader>

                <CardContent className="relative pb-8">
                  {/* Features List */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-foreground border-b pb-2"
                      style={{ borderColor: `rgba(var(--border), 0.5)` }}
                    >
                      What's included:
                    </h4>
                    <ul className="space-y-3">
                      {plan.features.map((feature, featureIndex) => (
                        <li 
                          key={featureIndex} 
                          className="flex items-start group/item"
                          style={{
                            animationDelay: `${featureIndex * 50}ms`
                          }}
                        >
                          <div 
                            className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 mr-3 transition-all duration-300 group-hover/item:scale-110"
                            style={{ background: `rgba(var(--theme-primary), 0.1)` }}
                          >
                            <Check className="w-3 h-3" style={{ color: `rgb(var(--theme-primary))` }} />
                          </div>
                          <span className="text-sm text-foreground/90 group-hover/item:text-foreground transition-colors">
                            {feature}
                          </span>
                        </li>
                      ))}
                      {plan.limitations.map((limitation, limitationIndex) => (
                        <li key={`limitation-${limitationIndex}`} className="flex items-start opacity-50">
                          <X className="w-5 h-5 text-muted-foreground mt-0.5 mr-3 flex-shrink-0" />
                          <span className="text-sm text-muted-foreground line-through">
                            {limitation}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Bottom Border Animation */}
                  <div 
                    className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-700 rounded-b-lg"
                    style={{
                      background: `linear-gradient(90deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                    }}
                  />
                </CardContent>
              </Card>
            )
          })}
        </div>

        {/* Bottom Info Card */}
        <div className="mt-20 animate-in fade-in slide-in-from-bottom duration-700 delay-300">
          <div className="max-w-4xl mx-auto glass-card rounded-2xl p-8 border"
            style={{
              borderColor: `rgba(var(--theme-primary), 0.3)`,
              background: `rgba(var(--theme-primary), 0.03)`
            }}
          >
            <div className="flex items-center justify-center mb-4">
              <Zap className="w-6 h-6 mr-2" style={{ color: `rgb(var(--theme-primary))` }} />
              <h3 className="text-lg font-semibold text-foreground">
                Lightning Fast Generation
              </h3>
            </div>
            <p className="text-center text-muted-foreground mb-6">
              Our AI generates stunning event pages in under 20 seconds with priority queues for paid plans. 
              Enterprise customers get fastest processing with dedicated resources.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { value: "< 20s", label: "Average generation", icon: Zap },
                { value: "99.2%", label: "Success rate", icon: TrendingUp },
                { value: "24/7", label: "System uptime", icon: Shield }
              ].map((stat, index) => {
                const IconComponent = stat.icon
                return (
                  <div key={index} className="text-center group cursor-pointer">
                    <div className="flex justify-center mb-2">
                      <div 
                        className="p-2 rounded-lg transition-all duration-300 group-hover:scale-110"
                        style={{ background: `rgba(var(--theme-primary), 0.1)` }}
                      >
                        <IconComponent className="w-5 h-5" style={{ color: `rgb(var(--theme-primary))` }} />
                      </div>
                    </div>
                    <div 
                      className="text-2xl font-bold mb-1 transition-all duration-300 group-hover:scale-110"
                      style={{
                        background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }}
                    >
                      {stat.value}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {stat.label}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
